/**
 * REMITANO WITHDRAWAL SERVICE
 * 
 * Automated cryptocurrency withdrawal service using Remitano's API.
 * Handles USDT withdrawals on supported networks (TRC20, ERC20, BEP20).
 * 
 * Features:
 * - Secure API authentication with access key/secret key
 * - HMAC-SHA256 request signing
 * - Comprehensive error handling with specific error codes
 * - Retry logic with exponential backoff
 * - Request/response logging for compliance
 * - Integration with existing withdrawal escrow system
 * - Admin-controlled 2FA input support
 * - Rate limiting protection
 * 
 * SECURITY:
 * - All credentials stored as environment variables (server-side only)
 * - No sensitive data exposed to client-side code
 * - All API calls logged for audit compliance
 * - 2FA codes never stored, only passed through
 */

import crypto from 'crypto';
import { getSessionHeaders } from '../serverSession';

// ==========================================
// TYPE DEFINITIONS
// ==========================================

export interface RemitanoConfig {
 apiKey: string;
 secretKey: string;
 baseUrl: string;
 dryRun: boolean;
 totpSecret?: string; // For automated 2FA if enabled
}

export interface WithdrawalRequest {
 withdrawalId: string;
 amount: number;
 walletAddress: string;
 network: 'TRC20' | 'ERC20' | 'BEP20';
 currency: string;
 adminId: number;
 twoFactorCode?: string;
}

export interface WithdrawalResult {
 success: boolean;
 transactionId?: string;
 remitanoTransactionId?: string;
 error?: RemitanoError;
 dryRun?: boolean;
}

export interface RemitanoError {
 code: RemitanoErrorCode;
 message: string;
 details?: string;
 retryable: boolean;
}

export type RemitanoErrorCode = 
 | 'REMITANO_INSUFFICIENT_BALANCE'
 | 'REMITANO_INVALID_2FA'
 | 'REMITANO_NETWORK_ERROR'
 | 'REMITANO_API_RATE_LIMIT'
 | 'REMITANO_INVALID_WALLET'
 | 'REMITANO_API_DOWN'
 | 'REMITANO_AUTH_FAILED'
 | 'REMITANO_INVALID_AMOUNT'
 | 'REMITANO_UNSUPPORTED_NETWORK'
 | 'REMITANO_WITHDRAWAL_DISABLED'
 | 'REMITANO_UNKNOWN_ERROR';

export interface RemitanoApiResponse<T = any> {
 success: boolean;
 data?: T;
 error?: {
 code: string;
 message: string;
 };
}

export interface RemitanoWithdrawResponse {
 id: string;
 status: 'pending' | 'processing' | 'completed' | 'failed';
 txHash?: string;
 amount: string;
 currency: string;
 network: string;
 address: string;
 fee: string;
 createdAt: string;
}

export interface RemitanoBalanceResponse {
 currency: string;
 available: string;
 locked: string;
}

export interface ApiLogEntry {
 withdrawalId?: string;
 adminId: number;
 endpoint: string;
 httpMethod: string;
 requestHeaders: Record<string, string>;
 responseStatus: number;
 responseHeaders?: Record<string, string>;
 errorCode?: string;
 errorMessage?: string;
 durationMs: number;
}

// ==========================================
// CONSTANTS
// ==========================================

const IS_BROWSER = typeof window !== 'undefined';

// Network mapping: Our network names -> Remitano network names
const NETWORK_MAPPING: Record<string, string> = {
 'TRC20': 'trc20',
 'TRON': 'trc20',
 'ERC20': 'erc20',
 'ETH': 'erc20',
 'BEP20': 'bep20',
 'BSC': 'bep20',
 'BNB': 'bep20'
};

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff in ms

// Rate limiting: max 10 calls per minute per admin
const RATE_LIMIT_WINDOW_MS = 60 * 1000;
const RATE_LIMIT_MAX_CALLS = 10;

// ==========================================
// RATE LIMITER CLASS
// ==========================================

class RateLimiter {
 private calls: Map<number, number[]> = new Map();

 isAllowed(adminId: number): boolean {
 const now = Date.now();
 const adminCalls = this.calls.get(adminId) || [];
 
 // Filter out calls outside the window
 const recentCalls = adminCalls.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
 this.calls.set(adminId, recentCalls);
 
 return recentCalls.length < RATE_LIMIT_MAX_CALLS;
 }

 recordCall(adminId: number): void {
 const calls = this.calls.get(adminId) || [];
 calls.push(Date.now());
 this.calls.set(adminId, calls);
 }

 getRemainingCalls(adminId: number): number {
 const adminCalls = this.calls.get(adminId) || [];
 const now = Date.now();
 const recentCalls = adminCalls.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
 return Math.max(0, RATE_LIMIT_MAX_CALLS - recentCalls.length);
 }
}

// ==========================================
// MAIN SERVICE CLASS
// ==========================================

export class RemitanoWithdrawalService {
 private config: RemitanoConfig;
 private rateLimiter = new RateLimiter();

 constructor() {
 // Security: Only load credentials on server-side
 if (IS_BROWSER) {
 // Silent in browser — credentials are not loaded, API calls proxy through server
 this.config = {
 apiKey: '',
 secretKey: '',
 baseUrl: '',
 dryRun: true
 };
 return;
 }

 // Load configuration from environment variables
 this.config = {
 apiKey: process.env.REMITANO_API_KEY || '',
 secretKey: process.env.REMITANO_SECRET_KEY || '',
 baseUrl: process.env.REMITANO_API_URL || 'https://api.remitano.com/api/v1',
 dryRun: process.env.REMITANO_DRY_RUN === 'true',
 totpSecret: process.env.REMITANO_TOTP_SECRET
 };

 if (this.config.dryRun) {
 console.log('[REMITANO] Dry run mode enabled - no actual API calls will be made');
 } else if (!this.config.apiKey || !this.config.secretKey) {
 console.warn('[REMITANO] API credentials not configured - service will fail on API calls');
 } else {
 console.log('[REMITANO] Withdrawal service initialized');
 }
 }

 /**
 * Check if the service is properly configured
 */
 isConfigured(): boolean {
 return !IS_BROWSER && !!this.config.apiKey && !!this.config.secretKey;
 }

 /**
 * Check if dry run mode is enabled
 */
 isDryRunMode(): boolean {
 return this.config.dryRun;
 }

 /**
 * Generate HMAC-SHA256 signature for API request
 */
 private generateSignature(timestamp: number, method: string, path: string, body?: string): string {
 const message = `${timestamp}${method.toUpperCase()}${path}${body || ''}`;
 return crypto
 .createHmac('sha256', this.config.secretKey)
 .update(message)
 .digest('hex');
 }

 /**
 * Generate TOTP code if secret is configured
 */
 private generateTOTPCode(): string | null {
 if (!this.config.totpSecret) {
 return null;
 }

 try {
 // Simple TOTP implementation (RFC 6238)
 const timeStep = 30;
 const time = Math.floor(Date.now() / 1000 / timeStep);
 const timeBuffer = Buffer.alloc(8);
 timeBuffer.writeBigInt64BE(BigInt(time));

 const key = Buffer.from(this.config.totpSecret, 'base32' as any);
 const hmac = crypto.createHmac('sha1', key).update(timeBuffer).digest();

 const offset = hmac[hmac.length - 1] & 0x0f;
 const code = ((hmac[offset] & 0x7f) << 24 |
 (hmac[offset + 1] & 0xff) << 16 |
 (hmac[offset + 2] & 0xff) << 8 |
 (hmac[offset + 3] & 0xff)) % 1000000;

 return code.toString().padStart(6, '0');
 } catch (error) {
 console.error('[REMITANO] Failed to generate TOTP code:', error);
 return null;
 }
 }

 /**
 * Map our network names to Remitano's network names
 */
 private mapNetwork(network: string): string {
 const normalized = network.toUpperCase();
 return NETWORK_MAPPING[normalized] || normalized.toLowerCase();
 }

 /**
 * Make authenticated API request to Remitano
 */
 private async makeApiRequest<T>(
 method: 'GET' | 'POST' | 'PUT' | 'DELETE',
 endpoint: string,
 body?: Record<string, any>,
 adminId?: number,
 withdrawalId?: string
 ): Promise<{ response: RemitanoApiResponse<T>; status: number; durationMs: number }> {
 const startTime = Date.now();

 try {
 const timestamp = Date.now();
 const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
 const bodyString = body ? JSON.stringify(body) : undefined;
 const signature = this.generateSignature(timestamp, method, path, bodyString);

 const headers: Record<string, string> = {
 'Content-Type': 'application/json',
 'X-Access-Key': this.config.apiKey,
 'X-Timestamp': timestamp.toString(),
 'X-Signature': signature
 };

 const response = await fetch(`${this.config.baseUrl}${path}`, {
 method,
 headers,
 body: bodyString
 });

 const durationMs = Date.now() - startTime;
 const responseData = await response.json() as RemitanoApiResponse<T>;

 // Log the API call (sanitized - no sensitive data)
 if (adminId) {
 await this.logApiCall({
 withdrawalId,
 adminId,
 endpoint: path,
 httpMethod: method,
 requestHeaders: { 'X-Access-Key': '[REDACTED]', 'X-Timestamp': timestamp.toString() },
 responseStatus: response.status,
 errorCode: responseData.error?.code,
 errorMessage: responseData.error?.message,
 durationMs
 });
 }

 return { response: responseData, status: response.status, durationMs };
 } catch (error) {
 const durationMs = Date.now() - startTime;

 if (adminId) {
 await this.logApiCall({
 withdrawalId,
 adminId,
 endpoint,
 httpMethod: method,
 requestHeaders: {},
 responseStatus: 0,
 errorCode: 'NETWORK_ERROR',
 errorMessage: error instanceof Error ? error.message : 'Unknown error',
 durationMs
 });
 }

 throw error;
 }
 }

 /**
 * Log API call to database for audit compliance
 */
 private async logApiCall(entry: ApiLogEntry): Promise<void> {
 try {
 await fetch('/api/admin/remitano-withdrawal', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ action: 'log_api_call', entry })
 });
 } catch (error) {
 // Don't fail the main operation if logging fails
 console.error('[REMITANO] Failed to log API call:', error);
 }
 }

 /**
 * Map API error response to our error codes
 */
 private mapErrorCode(errorCode: string, message: string): RemitanoError {
 const errorMapping: Record<string, RemitanoError> = {
 'insufficient_balance': {
 code: 'REMITANO_INSUFFICIENT_BALANCE',
 message: 'Insufficient balance in Remitano account',
 details: 'Please top up your Remitano account before processing withdrawals',
 retryable: false
 },
 'invalid_2fa': {
 code: 'REMITANO_INVALID_2FA',
 message: 'Invalid or expired 2FA code',
 details: 'Please enter a new 2FA code',
 retryable: true
 },
 'invalid_otp': {
 code: 'REMITANO_INVALID_2FA',
 message: 'Invalid or expired OTP code',
 details: 'Please enter a new 2FA code',
 retryable: true
 },
 'rate_limit': {
 code: 'REMITANO_API_RATE_LIMIT',
 message: 'API rate limit exceeded',
 details: 'Please wait before retrying',
 retryable: true
 },
 'invalid_address': {
 code: 'REMITANO_INVALID_WALLET',
 message: 'Invalid wallet address',
 details: 'The provided wallet address is not valid for the selected network',
 retryable: false
 },
 'unauthorized': {
 code: 'REMITANO_AUTH_FAILED',
 message: 'Authentication failed',
 details: 'API credentials may be invalid or expired',
 retryable: false
 },
 'service_unavailable': {
 code: 'REMITANO_API_DOWN',
 message: 'Remitano service is temporarily unavailable',
 details: 'Please try again later or process manually',
 retryable: true
 },
 'withdrawal_disabled': {
 code: 'REMITANO_WITHDRAWAL_DISABLED',
 message: 'Withdrawals are temporarily disabled',
 details: 'Remitano has disabled withdrawals. Please process manually.',
 retryable: false
 }
 };

 // Check if we have a mapped error
 const lowerCode = errorCode.toLowerCase();
 for (const [key, value] of Object.entries(errorMapping)) {
 if (lowerCode.includes(key)) {
 return { ...value, details: message || value.details };
 }
 }

 // Default unknown error
 return {
 code: 'REMITANO_UNKNOWN_ERROR',
 message: message || 'An unknown error occurred',
 details: `Original error code: ${errorCode}`,
 retryable: false
 };
 }

 /**
 * Retry operation with exponential backoff
 */
 private async withRetry<T>(
 operation: () => Promise<T>,
 maxRetries: number = MAX_RETRIES
 ): Promise<T> {
 let lastError: Error | null = null;

 for (let attempt = 0; attempt <= maxRetries; attempt++) {
 try {
 return await operation();
 } catch (error) {
 lastError = error instanceof Error ? error : new Error(String(error));

 // Check if error is retryable
 if (attempt < maxRetries) {
 const delay = RETRY_DELAYS[attempt] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
 console.log(`⏳ [REMITANO] Retry attempt ${attempt + 1}/${maxRetries} in ${delay}ms...`);
 await new Promise(resolve => setTimeout(resolve, delay));
 }
 }
 }

 throw lastError;
 }

 /**
 * Get available balance from Remitano
 */
 async getBalance(currency: string = 'USDT', adminId: number): Promise<{ available: number; locked: number } | null> {
 if (!this.isConfigured()) {
 console.warn('[REMITANO] Service not configured');
 return null;
 }

 try {
 const { response } = await this.makeApiRequest<RemitanoBalanceResponse[]>(
 'GET',
 '/merchant/balance',
 undefined,
 adminId
 );

 if (!response.success || !response.data) {
 return null;
 }

 const balance = response.data.find(b => b.currency.toUpperCase() === currency.toUpperCase());
 if (!balance) {
 return null;
 }

 return {
 available: parseFloat(balance.available),
 locked: parseFloat(balance.locked)
 };
 } catch (error) {
 console.error('[REMITANO] Failed to get balance:', error);
 return null;
 }
 }

 /**
 * Check if Remitano API is healthy
 */
 async healthCheck(adminId: number): Promise<{ healthy: boolean; message: string }> {
 if (!this.isConfigured()) {
 return { healthy: false, message: 'Service not configured' };
 }

 if (this.config.dryRun) {
 return { healthy: true, message: 'Dry run mode - API not checked' };
 }

 try {
 const { response, status } = await this.makeApiRequest(
 'GET',
 '/merchant/status',
 undefined,
 adminId
 );

 if (status === 200 && response.success) {
 return { healthy: true, message: 'Remitano API is operational' };
 }

 return { healthy: false, message: response.error?.message || 'API returned error' };
 } catch (error) {
 return {
 healthy: false,
 message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
 };
 }
 }

 /**
 * Process a withdrawal request through Remitano API
 * This is the main method for automated withdrawals
 */
 async processWithdrawal(request: WithdrawalRequest): Promise<WithdrawalResult> {
 const { withdrawalId, amount, walletAddress, network, adminId, twoFactorCode } = request;

 console.log(`[REMITANO] Processing withdrawal ${withdrawalId}: $${amount} ${network} to ${walletAddress}`);

 // Check rate limiting
 if (!this.rateLimiter.isAllowed(adminId)) {
 console.warn(`[REMITANO] Rate limit exceeded for admin ${adminId}`);
 return {
 success: false,
 error: {
 code: 'REMITANO_API_RATE_LIMIT',
 message: 'Too many requests. Please wait before trying again.',
 details: `Maximum ${RATE_LIMIT_MAX_CALLS} calls per minute allowed`,
 retryable: true
 }
 };
 }

 // Record the call for rate limiting
 this.rateLimiter.recordCall(adminId);

 // Check if dry run mode
 if (this.config.dryRun) {
 console.log(`[REMITANO] DRY RUN: Would process withdrawal ${withdrawalId}`);
 return {
 success: true,
 transactionId: `DRY_RUN_${Date.now()}`,
 remitanoTransactionId: `REMITANO_DRY_${Date.now()}`,
 dryRun: true
 };
 }

 // Check if service is configured
 if (!this.isConfigured()) {
 return {
 success: false,
 error: {
 code: 'REMITANO_AUTH_FAILED',
 message: 'Remitano API credentials not configured',
 details: 'Please configure REMITANO_API_KEY and REMITANO_SECRET_KEY environment variables',
 retryable: false
 }
 };
 }

 // Get 2FA code (from input or auto-generate)
 const otpCode = twoFactorCode || this.generateTOTPCode();
 if (!otpCode && !twoFactorCode) {
 return {
 success: false,
 error: {
 code: 'REMITANO_INVALID_2FA',
 message: '2FA code required',
 details: 'Please provide a 2FA code or configure REMITANO_TOTP_SECRET for auto-generation',
 retryable: true
 }
 };
 }

 try {
 // Map network to Remitano format
 const remitanoNetwork = this.mapNetwork(network);

 // Build withdrawal request
 const withdrawalPayload = {
 currency: 'usdt',
 network: remitanoNetwork,
 address: walletAddress,
 amount: amount.toString(),
 otp: otpCode
 };

 // Make the API request with retry logic
 const { response, status } = await this.withRetry(async () => {
 return await this.makeApiRequest<RemitanoWithdrawResponse>(
 'POST',
 '/merchant/withdraw',
 withdrawalPayload,
 adminId,
 withdrawalId
 );
 });

 // Handle response
 if (status === 200 && response.success && response.data) {
 console.log(`[REMITANO] Withdrawal successful: ${response.data.id}`);

 // Update withdrawal record with Remitano transaction ID
 await this.updateWithdrawalRecord(withdrawalId, {
 remitanoTransactionId: response.data.id,
 transactionHash: response.data.txHash,
 success: true
 });

 return {
 success: true,
 transactionId: response.data.txHash || response.data.id,
 remitanoTransactionId: response.data.id
 };
 }

 // Handle error response
 const error = this.mapErrorCode(
 response.error?.code || 'unknown',
 response.error?.message || 'Unknown error'
 );

 console.error(`[REMITANO] Withdrawal failed:`, error);

 // Update withdrawal record with error
 await this.updateWithdrawalRecord(withdrawalId, {
 success: false,
 errorCode: error.code,
 errorMessage: error.message
 });

 return { success: false, error };

 } catch (error) {
 const networkError: RemitanoError = {
 code: 'REMITANO_NETWORK_ERROR',
 message: 'Failed to connect to Remitano API',
 details: error instanceof Error ? error.message : 'Unknown error',
 retryable: true
 };

 console.error(`[REMITANO] Network error:`, networkError);

 // Update withdrawal record with error
 await this.updateWithdrawalRecord(withdrawalId, {
 success: false,
 errorCode: networkError.code,
 errorMessage: networkError.message
 });

 return { success: false, error: networkError };
 }
 }

 /**
 * Update withdrawal record with automation results
 */
 private async updateWithdrawalRecord(
 withdrawalId: string,
 result: {
 remitanoTransactionId?: string;
 transactionHash?: string;
 success: boolean;
 errorCode?: string;
 errorMessage?: string;
 }
 ): Promise<void> {
 try {
 await fetch('/api/admin/remitano-withdrawal', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ action: 'update_withdrawal_status', withdrawalId, result })
 });
 } catch (error) {
 console.error('[REMITANO] Failed to update withdrawal record:', error);
 }
 }

 /**
 * Get remaining API calls for an admin
 */
 getRemainingApiCalls(adminId: number): number {
 return this.rateLimiter.getRemainingCalls(adminId);
 }

 /**
 * Get automation statistics
 */
 async getAutomationStats(): Promise<{
 totalAttempted: number;
 totalSuccessful: number;
 totalFailed: number;
 successRate: number;
 recentFailures: Array<{ id: string; errorCode: string; errorMessage: string; createdAt: string }>;
 }> {
 try {
 const response = await fetch('/api/admin/remitano-withdrawal', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ action: 'get_automation_stats' })
 });
 const result = await response.json();
 if (!response.ok || !result.success) {
 throw new Error(result.error || 'Failed to get automation stats');
 }
 return result.data;
 } catch (error) {
 console.error('[REMITANO] Failed to get automation stats:', error);
 return {
 totalAttempted: 0,
 totalSuccessful: 0,
 totalFailed: 0,
 successRate: 0,
 recentFailures: []
 };
 }
 }
}

// Export singleton instance
export const remitanoWithdrawalService = new RemitanoWithdrawalService();
